import { useTranslation } from "react-i18next";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { SparklesIcon } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface OpportunityHeaderProps {
  title: string;
  type: string;
  createdBy: {
    id: string;
    fullName: string | null;
    avatarUrl: string | null;
  } | null;
}

export const OpportunityHeader = ({ title, type, createdBy }: OpportunityHeaderProps) => {
  const { t } = useTranslation();

  const { data: formattedType } = useQuery({
    queryKey: ['opportunity-type-format', type],
    queryFn: async () => {
      console.log('Fetching formatted type for:', type);
      const { data, error } = await supabase.functions.invoke('format-opportunity-type', {
        body: { type },
      });

      if (error) {
        console.error('Error formatting type:', error);
        return t(`opportunityCard.type.${type.toLowerCase()}`);
      }

      return data.formattedType;
    },
    initialData: t(`opportunityCard.type.${type.toLowerCase()}`),
  });

  return (
    <div className="flex items-start gap-4">
      <Avatar className="h-10 w-10">
        <AvatarImage src={createdBy?.avatarUrl || ''} />
        <AvatarFallback>
          {createdBy?.fullName?.charAt(0) || '?'}
        </AvatarFallback>
      </Avatar>
      <div>
        <h3 className="text-xl mb-2 flex items-center gap-2">
          {title}
          {type === 'audition' && (
            <SparklesIcon className="w-5 h-5 text-purple-500" />
          )}
        </h3>
        <p className="text-sm text-muted-foreground">
          {t('opportunityCard.postedBy')} {createdBy?.fullName || t('opportunityCard.anonymous')}
        </p>
        <Badge 
          variant={type === 'audition' ? 'secondary' : 'default'}
          className="mt-2"
        >
          {formattedType}
        </Badge>
      </div>
    </div>
  );
};